import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, FileX, Clock } from "lucide-react";

/**
 * PublicProposal component handles the public viewing of shared proposals.
 * 
 * It redirects to the backend endpoint which will:
 * 1. Validate the share token
 * 2. Mark the proposal as viewed
 * 3. Redirect to a signed CloudFront/S3 URL for the PDF
 */
export default function PublicProposal() {
  const { shareToken } = useParams();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!shareToken) {
      setError("Invalid proposal link");
      setIsLoading(false);
      return;
    }

    // Redirect to the backend endpoint which handles the PDF serving
    const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    
    // We'll fetch the URL and handle errors, then redirect on success
    const fetchProposal = async () => {
      try {
        // Make a HEAD request first to check if the proposal exists
        const response = await fetch(`${backendUrl}/p/${shareToken}`, {
          method: "GET",
          redirect: "manual", // Don't auto-follow redirects
        });

        if (response.status === 302 || response.status === 307) {
          // Get the redirect URL from the Location header
          const pdfUrl = response.headers.get("Location");
          if (pdfUrl) {
            // Redirect to the PDF
            window.location.href = pdfUrl;
            return;
          }
        }

        // If we get a direct redirect (opaque response), try opening directly
        if (response.type === "opaqueredirect") {
          window.location.href = `${backendUrl}/p/${shareToken}`;
          return;
        }

        // Handle error responses
        if (!response.ok) {
          const data = await response.json().catch(() => null);
          if (response.status === 404) {
            setError("Proposal not found. It may have been deleted.");
          } else if (response.status === 410) {
            setError("This proposal has expired.");
          } else {
            setError(data?.detail || "Unable to load proposal");
          }
          setIsLoading(false);
          return;
        }

        // If we get here, just redirect directly
        window.location.href = `${backendUrl}/p/${shareToken}`;
      } catch (err) {
        // On CORS errors or network issues, try direct redirect
        // This is expected behavior since the endpoint returns a redirect
        window.location.href = `${backendUrl}/p/${shareToken}`;
      }
    };

    fetchProposal();
  }, [shareToken]);

  // Loading state
  if (isLoading && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading proposal...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    const isExpired = error.includes("expired");
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          {isExpired ? (
            <Clock className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          ) : (
            <FileX className="w-16 h-16 text-red-400 mx-auto mb-4" />
          )}
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            {isExpired ? "Proposal Expired" : "Proposal Not Found"}
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            Please contact the sender for an updated link.
          </p>
        </div>
      </div>
    );
  }

  return null;
}
